import _styled, { default as _styled2 } from "styled-components";
var _templateObject;
const _excluded = ["value", "double", "calendarProps", "range", "startDate", "endDate", "minDate", "maxDate", "timeZone", "resetSelection", "onDateSelected", "onMonthChange", "firstDayOfWeek", "showOutsideDays", "isDateSelectable", "yearsToDisplay", "toggleOverlay", "hasEventForDate", "tooltipContentForDate", "renderFooter", "renderFooterActions", "className", "style", "shouldShowSelection"];
function _taggedTemplateLiteral(e, t) { return t || (t = e.slice(0)), Object.freeze(Object.defineProperties(e, { raw: { value: Object.freeze(t) } })); }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import { noop } from '@adsk/alloy-react-helpers';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import Calendar from './Calendar';
import DefaultFooter from './DefaultFooter';
import DefaultFooterActions from './DefaultFooterActions';
import { getSelectedDates } from '../helpers';
import useCalendars from '../hooks/useCalendars';
import useNavigation from '../hooks/useNavigation';
import useRangeHover from '../hooks/useRangeHover';
const DATE_PICKER_CALENDAR_WIDTH = 312;
const Overlay = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      value: dateProp,
      double,
      calendarProps,
      range,
      startDate,
      endDate,
      minDate,
      maxDate,
      timeZone,
      resetSelection,
      onDateSelected,
      onMonthChange = noop,
      firstDayOfWeek,
      showOutsideDays = false,
      isDateSelectable,
      yearsToDisplay = 20,
      toggleOverlay,
      hasEventForDate,
      tooltipContentForDate,
      renderFooter = p => /*#__PURE__*/React.createElement(DefaultFooter, p),
      renderFooterActions = p => /*#__PURE__*/React.createElement(DefaultFooterActions, p),
      className,
      style,
      shouldShowSelection
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  const [stateHoveredDate, onDateHover] = useRangeHover({
    range,
    startDate,
    endDate,
    minDate,
    maxDate
  });
  const {
    activeMonths,
    updateActiveMonths,
    setMonth,
    setYear,
    nextMonth,
    prevMonth,
    resetViewToTodayAndSelect
  } = useNavigation({
    range,
    value: dateProp,
    startDate,
    endDate,
    numberOfMonths: double ? 2 : 1,
    onDateSelected,
    onMonthChange,
    minDate,
    maxDate,
    timeZone
  });
  const selectedDates = getSelectedDates({
    range,
    date: dateProp,
    startDate,
    endDate
  });
  const calendars = useCalendars({
    activeMonths,
    selectedDates,
    minDate,
    maxDate,
    firstDayOfWeek,
    showOutsideDays,
    isDateSelectable,
    yearsToDisplay,
    timeZone
  });
  const totalCalendars = calendars.length;
  return /*#__PURE__*/React.createElement(_StyledDiv, _extends({
    ref: ref,
    className: className
  }, rest, {
    $_css: [{
      minHeight: 321,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.tokens.colors.background.primary.value,
      boxShadow: theme.shadows.highElevation,
      borderRadius: 6,
      padding: "".concat(theme.spacing.M, "px 0"),
      width: totalCalendars * DATE_PICKER_CALENDAR_WIDTH
    }, style]
  }), /*#__PURE__*/React.createElement(_StyledDiv2, null, calendars.map((calendar, key) => {
    return /*#__PURE__*/React.createElement(Calendar, _extends({
      key: key,
      range: range,
      calendar: calendar,
      calendars: calendars,
      stateHoveredDate: stateHoveredDate,
      onDateHover: onDateHover,
      setMonth: setMonth,
      setYear: setYear,
      nextMonth: nextMonth,
      prevMonth: prevMonth,
      onDateSelected: onDateSelected,
      updateActiveMonths: updateActiveMonths,
      hasEventForDate: hasEventForDate,
      tooltipContentForDate: tooltipContentForDate,
      startDate: startDate,
      endDate: endDate,
      minDate: minDate,
      maxDate: maxDate,
      shouldShowSelection: shouldShowSelection
    }, calendarProps));
  })), renderFooter === null || renderFooter === void 0 ? void 0 : renderFooter({
    today: resetViewToTodayAndSelect,
    reset: resetSelection,
    toggleOverlay,
    renderFooterActions
  }));
});
Overlay.displayName = 'Overlay';
Overlay.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Whether the we should display double date picker */
  double: PropTypes.bool,
  onDateSelected: PropTypes.func.isRequired,
  resetSelection: PropTypes.func.isRequired,
  toggleOverlay: PropTypes.func.isRequired,
  /** A selected date for a calendar without range */
  value: PropTypes.instanceOf(Date),
  /** A selected start date for a calendar with range data (should be used with range=true) */
  startDate: PropTypes.instanceOf(Date),
  /** A selected end date for a calendar with range data (should be used with range=true) */
  endDate: PropTypes.instanceOf(Date),
  /** Allow selection of dates after minDate, overrides `disablePastDates` */
  minDate: PropTypes.instanceOf(Date),
  /** Allow selection of dates before maxDate, overrides `disableFutureDates` */
  maxDate: PropTypes.instanceOf(Date),
  /** Flag whether the date picker is for a date range or a single value */
  range: PropTypes.bool,
  timeZone: PropTypes.string,
  /** A callback function called when the selected date(s) has been changed */
  onChange: PropTypes.func,
  /** A callback function called when the active months have changed (arrow click, month/year selection) */
  onMonthChange: PropTypes.func,
  /** Handler to manually check if date is selectable */
  isDateSelectable: PropTypes.func,
  /** First day of week to display */
  firstDayOfWeek: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]),
  /** Fill the blanks with days from prev/next month */
  showOutsideDays: PropTypes.bool,
  /* Props passed to each calendar */
  calendarProps: PropTypes.object,
  /** Render custom footer, executed in DatePickerOverlay */
  renderFooter: PropTypes.func,
  /** Render custom footer actions, executed in DatePickerOverlay */
  renderFooterActions: PropTypes.func,
  /** Determines if Day Cell should render an event dot */
  hasEventForDate: PropTypes.func,
  /** Tooltip content for Day Cell */
  tooltipContentForDate: PropTypes.func,
  /** How much years to display under and above current year, defaults to +-20 */
  yearsToDisplay: PropTypes.number
};
export default Overlay;
var _StyledDiv = _styled("div")(_templateObject || (_templateObject = _taggedTemplateLiteral(["", ""])), p => p.$_css);
var _StyledDiv2 = _styled2("div").withConfig({
  displayName: "Overlay___StyledDiv2",
  componentId: "sc-jwety7-0"
})({
  display: 'flex'
});
//# sourceMappingURL=Overlay.js.map